import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  UseDisclosureReturn,
  VStack,
} from "@chakra-ui/react";
import { getAccountShortName } from "@syla/shared/types/models/AccountAccessBase";
import React from "react";
import { useUserContext } from "../../../contexts/UserContext";
import { useMutationUpdateAccountAccess } from "../../../store/useMutationUpdateAccountAccess";
import { Account } from "../../../types/Account";
import { ConfirmActionModal } from "../../modals/ConfirmActionModal";

export function RemoveAccountModal({
  account,
  disclosure,
}: {
  account: Account;
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const { userDetails } = useUserContext();

  const mutationUpdateAccountAccess = useMutationUpdateAccountAccess(
    account._id,
  );

  const removeAccountAccess = async () => {
    if (!userDetails?.id) {
      throw new Error(
        "Unable to remove account access because the current user is missing.",
      );
    }

    await mutationUpdateAccountAccess.mutateAsync({
      ops: [
        {
          action: "delete",
          userId: userDetails?.id,
        },
      ],
    });
  };

  return (
    <ConfirmActionModal
      disclosure={disclosure}
      title="Remove yourself from account"
      confirmButtonText="Remove account"
      colorScheme="red"
      onConfirm={removeAccountAccess}
    >
      <VStack spacing={6} align="stretch">
        <Text>
          You are removing yourself from the account:{" "}
          <Text as="span" fontWeight="bold">
            {getAccountShortName(account)}
          </Text>
        </Text>

        <Text>
          This will only remove your access to the account. The account will not
          be deleted.
        </Text>

        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />

          <Box>
            <AlertTitle mb={2}>Warning</AlertTitle>

            <AlertDescription>
              You’ll only be able to regain access to the account by asking the
              account owner.
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>
    </ConfirmActionModal>
  );
}
